import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  /** Legacy signature used by components/common/Toast consumers. 'warning' renders as info. */
  showToast: (message: string, type: ToastVariant | 'warning') => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const MAX_TOASTS = 5;
// Errors stay longer so they can be read; all toasts pause while hovered or focused.
const DURATION: Record<ToastVariant, number> = { success: 4000, info: 5000, error: 8000 };

const STYLES: Record<ToastVariant, { box: string; icon: string }> = {
  success: { box: 'border-l-green-600', icon: 'text-green-600' },
  error: { box: 'border-l-red-600', icon: 'text-red-600' },
  info: { box: 'border-l-brand-navy', icon: 'text-brand-navy' },
};

const ICONS = { success: CheckCircle2, error: XCircle, info: Info };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const schedule = useCallback(
    (id: number, variant: ToastVariant) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(id, setTimeout(() => dismiss(id), DURATION[variant]));
    },
    [dismiss]
  );

  const pause = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
  }, []);

  const push = useCallback(
    (message: string, variant: ToastVariant) => {
      const id = ++counter.current;
      setToasts((prev) => {
        const next = [...prev, { id, message, variant }];
        while (next.length > MAX_TOASTS) {
          const removed = next.shift();
          if (removed) pause(removed.id);
        }
        return next;
      });
      schedule(id, variant);
    },
    [schedule, pause]
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach(clearTimeout);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
      info: (m) => push(m, 'info'),
      showToast: (m, type) => push(m, type === 'warning' ? 'info' : type),
    }),
    [push]
  );

  const polite = toasts.filter((t) => t.variant !== 'error');
  const assertive = toasts.filter((t) => t.variant === 'error');

  const renderList = (items: ToastItem[]) =>
    items.map((t) => {
      const Icon = ICONS[t.variant];
      return (
        <li
          key={t.id}
          onMouseEnter={() => pause(t.id)}
          onMouseLeave={() => schedule(t.id, t.variant)}
          onFocus={() => pause(t.id)}
          onBlur={() => schedule(t.id, t.variant)}
          className={cn(
            'pointer-events-auto flex w-full items-start gap-3 rounded-[12px] border border-l-4 border-surface-sunken bg-surface-card p-4 shadow-card-lg',
            'motion-safe:animate-slide-in',
            STYLES[t.variant].box
          )}
        >
          <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', STYLES[t.variant].icon)} aria-hidden="true" />
          <p className="flex-1 whitespace-pre-line text-sm font-medium text-ink-primary">{t.message}</p>
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            aria-label="Dismiss notification"
            className="flex-shrink-0 rounded-sm text-ink-muted hover:text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-navy-400"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </li>
      );
    });

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-end gap-2 sm:left-auto sm:right-4 sm:w-96">
        <ol role="status" aria-live="polite" aria-label="Notifications" className="flex w-full flex-col gap-2">
          {renderList(polite)}
        </ol>
        <ol role="alert" aria-live="assertive" aria-label="Errors" className="flex w-full flex-col gap-2">
          {renderList(assertive)}
        </ol>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
